/**
 * 预警看板（合同预警 / 费用预警共用）
 *
 * 【为什么做成一个组件而不是两个页面】合同预警与费用预警的形状完全一样
 * （类型、等级、触发依据、处理状态），差别只有数据源、链接前缀与权限码。
 * 抄两份的代价不是多写一遍，而是**处理规则会分叉**——
 * 比如"忽略必须填理由"这条改了一处忘了另一处，就成了一个能被静默绕过的口子。
 *
 * 【处理语义】确认（ACK）与忽略（DISMISSED）都要填说明并写审计。
 * 忽略一条"超开票 3000 元"等于做了"这属于正常情况"的判断，
 * 不留下判据，下次同一条预警再出现时没人知道上次为什么放它过去。
 */

import { useState } from 'react';
import { Alert, Button, Card, Space, Tag, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import type { BizAlert } from '@ilc/shared';
import {
  AlertStatus,
  ALERT_STATUS_LABELS,
  PermissionCode,
  RiskLevel,
  RISK_LEVEL_LABELS,
} from '@ilc/shared';

/** 预警列表查询条件。`level` 收紧成 RiskLevel，与 shared 的契约对齐 */
export interface AlertQuery {
  page?: number;
  pageSize?: number;
  alertType?: string;
  level?: RiskLevel;
  status?: AlertStatus;
}
import { DataTable } from '@/components/DataTable';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { feedback } from '@/utils/feedback';

export interface AlertBoardProps {
  title: string;
  description: React.ReactNode;
  /** 数据源：合同或费用 */
  alerts: (query: AlertQuery) => Promise<{
    page: number;
    pageSize: number;
    total: number;
    list: BizAlert[];
  }>;
  /** 处理（确认/忽略） */
  handle: (id: string, body: { status: 'ACK' | 'DISMISSED'; note: string }) => Promise<unknown>;
  /** 处理预警所需的权限码 */
  handlePermission: string;
  /** 预警类型筛选项（各模块不同） */
  typeOptions: Array<{ value: string; label: string }>;
  /** 页脚的口径说明 */
  footnote: React.ReactNode;
}

export function AlertBoard(props: AlertBoardProps): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canHandle = can(props.handlePermission);

  const list = usePagedList<BizAlert, AlertQuery>({
    loader: (q) => props.alerts(q),
    // 默认只看待处理的：预警列表堆满已处理的历史，人就再也不看了
    initialQuery: { page: 1, pageSize: 20, status: AlertStatus.OPEN },
  });

  const [ackTarget, setAckTarget] = useState<BizAlert | null>(null);
  const [dismissTarget, setDismissTarget] = useState<BizAlert | null>(null);

  const doHandle = async (alert: BizAlert, status: 'ACK' | 'DISMISSED', note: string): Promise<void> => {
    try {
      await props.handle(alert.id, { status, note });
      feedback.success(status === 'ACK' ? '已确认该预警' : '已忽略该预警');
      setAckTarget(null);
      setDismissTarget(null);
      list.reload();
    } catch (err) {
      // 409 表示别人刚处理过——提示刷新而不是当成失败重试
      feedback.error(err instanceof Error ? err.message : '处理失败');
      list.reload();
    }
  };

  const columns = [
    {
      title: '等级',
      dataIndex: 'level',
      width: 76,
      render: (v: string) => {
        const color =
          v === RiskLevel.HIGH ? 'error' : v === RiskLevel.MEDIUM ? 'warning' : 'default';
        return <Tag color={color}>{RISK_LEVEL_LABELS[v as RiskLevel] ?? v}</Tag>;
      },
    },
    {
      title: '类型',
      dataIndex: 'alertTypeLabel',
      width: 130,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: '指向单据',
      dataIndex: 'subjectLabel',
      width: 240,
      ellipsis: true,
      render: (v: string, r: BizAlert) =>
        r.subjectLink ? <Link to={r.subjectLink}>{v || '（未命名）'}</Link> : v || '-',
    },
    {
      title: '说明',
      dataIndex: 'message',
      ellipsis: true,
      render: (v: string) => (
        // 预警文案本身就是给人读的完整句，不截断语义——只做视觉截断 + 悬浮看全文
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>}>
          <span>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: '检出时间',
      dataIndex: 'detectedAt',
      width: 150,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => {
        const color = v === AlertStatus.OPEN ? 'processing' : v === AlertStatus.ACK ? 'success' : 'default';
        return <Tag color={color}>{ALERT_STATUS_LABELS[v as AlertStatus] ?? v}</Tag>;
      },
    },
    {
      title: '处理',
      key: 'handled',
      width: 200,
      render: (_: unknown, r: BizAlert) =>
        r.status === AlertStatus.OPEN ? (
          <Space size={4}>
            {/* 不传 permission 时按钮都不该出现——但处理仍要在后端再校验一次 */}
            <Button size="small" disabled={!canHandle} onClick={() => setAckTarget(r)}>
              确认
            </Button>
            <Button size="small" disabled={!canHandle} onClick={() => setDismissTarget(r)}>
              忽略
            </Button>
          </Space>
        ) : (
          <Tooltip title={r.handleNote || '（未填说明）'}>
            <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {r.handledByName ?? '—'} · {r.handledAt ? dayjs(r.handledAt).format('MM-DD HH:mm') : ''}
            </span>
          </Tooltip>
        ),
    },
  ];

  return (
    <div className="ilc-page">
      <PageHeader
        title={props.title}
        description={props.description}
        extra={
          <Space>
            <Button onClick={list.reload}>刷新</Button>
          </Space>
        }
      />

      {!canHandle && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="你没有处理预警的权限，本页只读"
          description="需要「确认 / 忽略」权限才能处理。请联系租户管理员在「角色权限」里分配。"
        />
      )}

      <DataTable<BizAlert>
        storageKey="alert-board"
        exportFileName={props.title}
        exportColumns={[
          { title: '等级', value: (r) => RISK_LEVEL_LABELS[r.level] ?? r.level },
          { title: '类型', dataIndex: 'alertTypeLabel' },
          { title: '指向单据', dataIndex: 'subjectLabel' },
          { title: '说明', dataIndex: 'message' },
          { title: '检出时间', value: (r) => dayjs(r.detectedAt).format('YYYY-MM-DD HH:mm') },
          { title: '状态', value: (r) => ALERT_STATUS_LABELS[r.status] ?? r.status },
          { title: '处理人', dataIndex: 'handledByName' },
          { title: '处理说明', dataIndex: 'handleNote' },
        ]}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        scrollX={1200}
        emptyText="当前没有符合条件的预警"
        toolbar={
          <Space wrap>
            <Space.Compact>
              <Button
                type={list.query.status === AlertStatus.OPEN ? 'primary' : 'default'}
                onClick={() => list.setQuery({ status: AlertStatus.OPEN })}
              >
                待处理
              </Button>
              <Button
                type={list.query.status === undefined ? 'primary' : 'default'}
                onClick={() => list.setQuery({ status: undefined })}
              >
                全部
              </Button>
            </Space.Compact>
            {props.typeOptions.length > 0 && (
              <span>
                类型：
                <Button
                  size="small"
                  type={list.query.alertType === undefined ? 'link' : 'text'}
                  onClick={() => list.setQuery({ alertType: undefined })}
                >
                  全部
                </Button>
                {props.typeOptions.map((o) => (
                  <Button
                    key={o.value}
                    size="small"
                    type={list.query.alertType === o.value ? 'link' : 'text'}
                    onClick={() => list.setQuery({ alertType: o.value })}
                  >
                    {o.label}
                  </Button>
                ))}
              </span>
            )}
          </Space>
        }
      />

      <Card size="small" title="这些预警是怎么来的" style={{ marginTop: 16 }}>
        {props.footnote}
      </Card>

      <ConfirmDanger
        open={ackTarget !== null}
        title={`确认预警：${ackTarget?.alertTypeLabel ?? ''}`}
        description={
          <span>
            {ackTarget?.message}
            <br />
            <br />
            确认表示<strong>问题属实、已知悉</strong>（预警关闭）。若不打算处理、判定为误报，请用「忽略」。
          </span>
        }
        level="normal"
        reasonLabel="处理说明（必填，将写入操作日志）"
        minReasonLength={4}
        confirmText="确认知悉"
        onConfirm={(note) => (ackTarget ? void doHandle(ackTarget, 'ACK', note) : undefined)}
        onCancel={() => setAckTarget(null)}
      />

      <ConfirmDanger
        open={dismissTarget !== null}
        title={`忽略预警：${dismissTarget?.alertTypeLabel ?? ''}`}
        description={
          <span>
            {dismissTarget?.message}
            <br />
            <br />
            忽略表示判定为<strong>误报或无需处理</strong>。理由会写进操作日志——
            下次同一条预警再出现时，别人要能查到这次为什么放它过去。
          </span>
        }
        level="high"
        reasonLabel="忽略理由（必填，将写入操作日志）"
        minReasonLength={8}
        reasonPresets={['已核实为正常业务', '补充协议尚未录入，后续补录', '数据重复，已人工合并']}
        confirmText="确认忽略"
        onConfirm={(note) => (dismissTarget ? void doHandle(dismissTarget, 'DISMISSED', note) : undefined)}
        onCancel={() => setDismissTarget(null)}
      />
    </div>
  );
}

export default AlertBoard;

/** 供页面复用的权限码常量，避免在 JSX 里写字符串字面量 */
export const AlertPermissions = { CONTRACT: PermissionCode.CONTRACT_ALERT_HANDLE, EXPENSE: PermissionCode.EXPENSE_ALERT_HANDLE };
